import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentServices.css";

const EMPTY_FORM = {
  name: "",
  public_description: "",
  internal_description: "",
  default_price: "",
  pricing_type: "custom",
  active: true,
  featured: false,
  display_order: "0",
};

const PRICING_TYPES = [
  "custom",
  "fixed",
  "hourly",
  "monthly",
  "starting_at",
];

function normalizeService(service = {}) {
  return {
    ...EMPTY_FORM,
    ...service,

    default_price:
      service.default_price === null ||
      service.default_price === undefined
        ? ""
        : String(service.default_price),

    display_order:
      service.display_order === null ||
      service.display_order === undefined
        ? "0"
        : String(service.display_order),

    active:
      Number(service.active) === 1,

    featured:
      Number(service.featured) === 1,
  };
}

function formatCurrency(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "Custom";
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "Custom";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function DevelopmentServices() {
  const [services, setServices] =
    useState([]);

  const [selectedService, setSelectedService] =
    useState(null);

  const [formData, setFormData] =
    useState(EMPTY_FORM);

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("active");

  const [pricingFilter, setPricingFilter] =
    useState("");

  const [showForm, setShowForm] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadServices = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const response =
          await Api.getDevelopmentServices();

        setServices(
          response?.services || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Services could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const filteredServices = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return services.filter((service) => {
      const isActive =
        Number(service.active) === 1;

      if (
        statusFilter === "active" &&
        !isActive
      ) {
        return false;
      }

      if (
        statusFilter === "inactive" &&
        isActive
      ) {
        return false;
      }

      if (
        statusFilter === "featured" &&
        Number(service.featured) !== 1
      ) {
        return false;
      }

      if (
        pricingFilter &&
        service.pricing_type !==
          pricingFilter
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchableText = [
        service.name,
        service.public_description,
        service.internal_description,
        service.pricing_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch
      );
    });
  }, [
    services,
    search,
    statusFilter,
    pricingFilter,
  ]);

  const summary = useMemo(() => {
    return {
      total: services.length,

      active: services.filter(
        (service) =>
          Number(service.active) === 1
      ).length,

      inactive: services.filter(
        (service) =>
          Number(service.active) !== 1
      ).length,

      featured: services.filter(
        (service) =>
          Number(service.featured) === 1
      ).length,
    };
  }, [services]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewServiceForm = () => {
    clearMessages();
    setSelectedService(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditServiceForm = (
    service
  ) => {
    clearMessages();
    setSelectedService(service);
    setFormData(
      normalizeService(service)
    );
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setSelectedService(null);
    setFormData(EMPTY_FORM);
    setShowForm(false);
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return "Service name is required.";
    }

    if (
      formData.default_price !== ""
    ) {
      const price = Number(
        formData.default_price
      );

      if (
        !Number.isFinite(price) ||
        price < 0
      ) {
        return "Default price must be 0 or greater.";
      }
    }

    const displayOrder = Number(
      formData.display_order
    );

    if (
      !Number.isInteger(displayOrder) ||
      displayOrder < 0
    ) {
      return "Display order must be a whole number that is 0 or greater.";
    }

    return "";
  };

  const buildPayload = () => ({
    name:
      formData.name.trim(),

    public_description:
      formData.public_description.trim() ||
      null,

    internal_description:
      formData.internal_description.trim() ||
      null,

    default_price:
      formData.default_price === ""
        ? null
        : Number(
            formData.default_price
          ),

    pricing_type:
      formData.pricing_type,

    active:
      Boolean(formData.active),

    featured:
      Boolean(formData.featured),

    display_order:
      Number(
        formData.display_order || 0
      ),
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();
    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (selectedService) {
        await Api.updateDevelopmentService(
          selectedService.id,
          payload
        );

        setSuccess(
          "Service updated successfully."
        );
      } else {
        await Api.createDevelopmentService(
          payload
        );

        setSuccess(
          "Service created successfully."
        );
      }

      await loadServices();
      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The service could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updateService = async (
    service,
    changes,
    successMessage
  ) => {
    clearMessages();

    try {
      await Api.updateDevelopmentService(
        service.id,
        changes
      );

      setSuccess(successMessage);
      await loadServices();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The service could not be updated."
      );
    }
  };

  const toggleActive = async (
    service
  ) => {
    const nextActive =
      Number(service.active) !== 1;

    const action =
      nextActive
        ? "reactivate"
        : "deactivate";

    const confirmed =
      window.confirm(
        `Are you sure you want to ${action} "${service.name}"?`
      );

    if (!confirmed) {
      return;
    }

    await updateService(
      service,
      {
        active: nextActive,
      },
      nextActive
        ? "Service reactivated."
        : "Service deactivated."
    );
  };

  const toggleFeatured = async (
    service
  ) => {
    const nextFeatured =
      Number(service.featured) !== 1;

    await updateService(
      service,
      {
        featured: nextFeatured,
      },
      nextFeatured
        ? "Service marked as featured."
        : "Service removed from featured services."
    );
  };

  const moveService = async (
    service,
    direction
  ) => {
    const orderedServices = [
      ...services,
    ].sort(
      (a, b) =>
        Number(a.display_order || 0) -
          Number(b.display_order || 0) ||
        Number(a.id) - Number(b.id)
    );

    const currentIndex =
      orderedServices.findIndex(
        (item) =>
          Number(item.id) ===
          Number(service.id)
      );

    const targetIndex =
      currentIndex + direction;

    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >=
        orderedServices.length
    ) {
      return;
    }

    const targetService =
      orderedServices[targetIndex];

    const currentOrder =
      Number(
        service.display_order || 0
      );

    const targetOrder =
      Number(
        targetService.display_order || 0
      );

    try {
      await Promise.all([
        Api.updateDevelopmentService(
          service.id,
          {
            display_order:
              targetOrder,
          }
        ),

        Api.updateDevelopmentService(
          targetService.id,
          {
            display_order:
              currentOrder,
          }
        ),
      ]);

      setSuccess(
        "Service order updated."
      );

      await loadServices();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The service order could not be updated."
      );
    }
  };

  return (
    <div className="development-services-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Service management
          </p>

          <h2>Services</h2>

          <p>
            Manage reusable Development
            services, pricing defaults,
            public descriptions, and display
            order.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              openNewServiceForm
            }
          >
            Add service
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <ServiceForm
          formData={formData}
          selectedService={
            selectedService
          }
          isSaving={isSaving}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-service-summary-grid">
        <SummaryCard
          label="All services"
          value={summary.total}
          active={
            statusFilter === "all"
          }
          onClick={() =>
            setStatusFilter("all")
          }
        />

        <SummaryCard
          label="Active"
          value={summary.active}
          active={
            statusFilter === "active"
          }
          onClick={() =>
            setStatusFilter("active")
          }
        />

        <SummaryCard
          label="Inactive"
          value={summary.inactive}
          active={
            statusFilter === "inactive"
          }
          onClick={() =>
            setStatusFilter("inactive")
          }
        />

        <SummaryCard
          label="Featured"
          value={summary.featured}
          active={
            statusFilter === "featured"
          }
          onClick={() =>
            setStatusFilter("featured")
          }
        />
      </section>

      <section className="development-services-panel">
        <div className="development-services-toolbar">
          <div>
            <h3>Service records</h3>

            <p>
              {filteredServices.length} service
              {filteredServices.length === 1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-services-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search services..."
              aria-label="Search services"
            />

            <select
              value={pricingFilter}
              onChange={(event) =>
                setPricingFilter(
                  event.target.value
                )
              }
              aria-label="Filter by pricing type"
            >
              <option value="">
                All pricing types
              </option>

              {PRICING_TYPES.map(
                (pricingType) => (
                  <option
                    key={pricingType}
                    value={pricingType}
                  >
                    {formatLabel(
                      pricingType
                    )}
                  </option>
                )
              )}
            </select>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              aria-label="Filter service status"
            >
              <option value="all">
                All services
              </option>

              <option value="active">
                Active services
              </option>

              <option value="inactive">
                Inactive services
              </option>

              <option value="featured">
                Featured services
              </option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading services...
          </div>
        ) : filteredServices.length ===
          0 ? (
          <div className="development-admin-empty">
            No services match the current
            filters.
          </div>
        ) : (
          <div className="development-service-card-grid">
            {filteredServices.map(
              (service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onEdit={() =>
                    openEditServiceForm(
                      service
                    )
                  }
                  onToggleActive={() =>
                    toggleActive(service)
                  }
                  onToggleFeatured={() =>
                    toggleFeatured(
                      service
                    )
                  }
                  onMoveUp={() =>
                    moveService(
                      service,
                      -1
                    )
                  }
                  onMoveDown={() =>
                    moveService(
                      service,
                      1
                    )
                  }
                />
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-service-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function ServiceCard({
  service,
  onEdit,
  onToggleActive,
  onToggleFeatured,
  onMoveUp,
  onMoveDown,
}) {
  const isActive =
    Number(service.active) === 1;

  const isFeatured =
    Number(service.featured) === 1;

  return (
    <article
      className={`development-service-card${
        !isActive ? " inactive" : ""
      }`}
    >
      <div className="development-service-card-heading">
        <div>
          <p className="development-service-order">
            Display order:{" "}
            {service.display_order || 0}
          </p>

          <h4>{service.name}</h4>
        </div>

        <div className="development-service-badges">
          {isFeatured && (
            <span className="featured">
              Featured
            </span>
          )}

          <span
            className={
              isActive
                ? "active"
                : "inactive"
            }
          >
            {isActive
              ? "Active"
              : "Inactive"}
          </span>
        </div>
      </div>

      <div className="development-service-price-row">
        <strong>
          {formatCurrency(
            service.default_price
          )}
        </strong>

        <span>
          {formatLabel(
            service.pricing_type
          )}
        </span>
      </div>

      <div className="development-service-description-block">
        <h5>Public description</h5>

        <p>
          {service.public_description ||
            "No public description has been added."}
        </p>
      </div>

      <div className="development-service-description-block internal">
        <h5>Internal description</h5>

        <p>
          {service.internal_description ||
            "No internal description has been added."}
        </p>
      </div>

      <div className="development-service-card-footer">
        <div className="development-service-order-buttons">
          <button
            type="button"
            onClick={onMoveUp}
            aria-label={`Move ${service.name} up`}
          >
            ↑
          </button>

          <button
            type="button"
            onClick={onMoveDown}
            aria-label={`Move ${service.name} down`}
          >
            ↓
          </button>
        </div>

        <div className="development-service-actions">
          <button
            type="button"
            onClick={onEdit}
          >
            Edit
          </button>

          <button
            type="button"
            className={
              isFeatured
                ? "unfeature"
                : "feature"
            }
            onClick={
              onToggleFeatured
            }
          >
            {isFeatured
              ? "Unfeature"
              : "Feature"}
          </button>

          <button
            type="button"
            className={
              isActive
                ? "deactivate"
                : "reactivate"
            }
            onClick={onToggleActive}
          >
            {isActive
              ? "Deactivate"
              : "Reactivate"}
          </button>
        </div>
      </div>
    </article>
  );
}

function ServiceForm({
  formData,
  selectedService,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-service-form-panel">
      <div className="development-service-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedService
              ? "Edit service"
              : "New service"}
          </p>

          <h3>
            {selectedService
              ? selectedService.name
              : "Create service"}
          </h3>
        </div>

        <button
          type="button"
          className="development-service-close-button"
          onClick={onCancel}
          aria-label="Close service form"
        >
          ×
        </button>
      </div>

      <form
        className="development-service-form"
        onSubmit={onSubmit}
      >
        <div className="development-service-form-grid">
          <label className="development-service-field full-width">
            <span>
              Service name
              <span className="required-marker">
                *
              </span>
            </span>

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={onChange}
              required
            />
          </label>

          <label className="development-service-field">
            <span>Pricing type</span>

            <select
              name="pricing_type"
              value={
                formData.pricing_type
              }
              onChange={onChange}
            >
              {PRICING_TYPES.map(
                (pricingType) => (
                  <option
                    key={pricingType}
                    value={pricingType}
                  >
                    {formatLabel(
                      pricingType
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <label className="development-service-field">
            <span>Default price</span>

            <input
              type="number"
              name="default_price"
              value={
                formData.default_price
              }
              onChange={onChange}
              min="0"
              step="0.01"
              placeholder="Leave blank for custom pricing"
            />
          </label>

          <label className="development-service-field">
            <span>Display order</span>

            <input
              type="number"
              name="display_order"
              value={
                formData.display_order
              }
              onChange={onChange}
              min="0"
              step="1"
            />
          </label>

          <div className="development-service-checkbox-group">
            <label className="development-service-checkbox">
              <input
                type="checkbox"
                name="active"
                checked={
                  formData.active
                }
                onChange={onChange}
              />

              <span>
                Service is active
              </span>
            </label>

            <label className="development-service-checkbox">
              <input
                type="checkbox"
                name="featured"
                checked={
                  formData.featured
                }
                onChange={onChange}
              />

              <span>
                Feature this service
              </span>
            </label>
          </div>

          <label className="development-service-field full-width">
            <span>
              Public description
            </span>

            <textarea
              name="public_description"
              value={
                formData.public_description
              }
              onChange={onChange}
              rows="5"
              placeholder="Description shown publicly or in proposals..."
            />
          </label>

          <label className="development-service-field full-width">
            <span>
              Internal description
            </span>

            <textarea
              name="internal_description"
              value={
                formData.internal_description
              }
              onChange={onChange}
              rows="5"
              placeholder="Private notes about pricing, scope, or delivery..."
            />
          </label>
        </div>

        <div className="development-service-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedService
                ? "Save changes"
                : "Create service"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default DevelopmentServices;